import layout from 'src/layout/store/state'
import PATHS from 'src/router/paths'

const { icons: { navigation: navIcons } } = layout()

const makeQueryStringFromObject = (obj) => Object.keys(obj).reduce((queryString, key) => {
  const value = obj[key]
  const newQueryString = queryString === '?' ? queryString : `${queryString}&`
  return value ? `${newQueryString}${key}=${value}` : newQueryString
}, '?')
const makeQueryString = (associationId, subId, reportPath, action = 'read') => (associationId ? makeQueryStringFromObject({
  action: action || 'read',
  associationId: associationId || 'all-associations',
  reportPath: reportPath || 'all-data',
  subId: subId || 'all-secondary',
})
  : '')

const navigation = () => ({
  endpoints: {
    [PATHS.HOME.BASE]: {
      access: (rootGetters) => rootGetters['auth/isActiveUser'],
      active: false,
      divider: 'post',
      image: navIcons.home,
      baseReportName: null,
      routeName: PATHS.DASHBOARD.BASE,
      href() {
        return '/'
      },
      title: 'Home',
    },
    [PATHS.DASHBOARD.BASE]: {
      access: (rootGetters) => rootGetters['auth/isActiveUser'],
      active: false,
      divider: false,
      header: (selectedSearch) => (selectedSearch && selectedSearch.routeName !== 'internal' && selectedSearch.orgName ? selectedSearch.orgName : 'All Networks'),
      image: navIcons.dashboard,
      baseReportName: null,
      routeName: PATHS.DASHBOARD.BASE,
      href(associationId, subId, reportPath = 'all-data') {
        const action = 'read'
        const subString = makeQueryString(associationId, subId, reportPath, action)
        return `/dashboard${subString}`
      },
      title: 'Network',
    },
    [PATHS.DELIVERY.PACING]: {
      access: (rootGetters) => rootGetters['auth/isActiveUser'],
      active: false,
      divider: false,
      href(associationId = 'all-associations', subId = 'all-secondary', reportPath = 'all-data') {
        const isAllAssociations = associationId === 'all-associations'
        const action = isAllAssociations ? 'edit' : 'read'
        const subString = makeQueryString(associationId, subId, reportPath, action)
        return `/delivery/pacing${subString}`
      },
      image: navIcons[PATHS.DELIVERY.PACING],
      baseReportName: PATHS.DELIVERY.PACING,
      routeName: PATHS.DELIVERY.BASE,
      subItems: false,
      title: 'Pacing',
    },
    [PATHS.DELIVERY.DISCREPANCY]: {
      access: (rootGetters) => rootGetters['tools/hasDiscrepancy'],
      active: false,
      divider: false,
      href(associationId = 'all-associations', subId = 'all-secondary', reportPath = 'all-data') {
        const isAllAssociations = associationId === 'all-associations'
        const action = isAllAssociations ? 'edit' : 'read'
        const subString = makeQueryString(associationId, subId, reportPath, action)
        return `/delivery/discrepancy${subString}`
      },
      image: navIcons[PATHS.DELIVERY.DISCREPANCY],
      baseReportName: PATHS.DELIVERY.DISCREPANCY,
      routeName: PATHS.DELIVERY.BASE,
      subItems: false,
      title: 'Discrepancy',
    },
    [PATHS.PROGRAMMATIC.NETWORK_HEALTH]: {
      access: (rootGetters) => {
        const isInternalUser = rootGetters['auth/isInternalUser']
        const hasHealthNetwork = rootGetters['tools/hasHealthNetwork']
        const isProgrammaticUser = rootGetters['auth/isProgrammaticUser']

        return isInternalUser || (hasHealthNetwork && isProgrammaticUser)
      },
      active: false,
      divider: false,
      href(associationId = 'all-associations', subId = 'all-secondary', reportPath = 'all-data') {
        const isAllAssociations = associationId === 'all-associations'
        const action = isAllAssociations ? 'edit' : 'read'
        const subString = makeQueryString(associationId, subId, reportPath, action)
        return `/programmatic/${PATHS.PROGRAMMATIC.NETWORK_HEALTH}${subString}`
      },
      image: navIcons[PATHS.PROGRAMMATIC.NETWORK_HEALTH],
      baseReportName: PATHS.PROGRAMMATIC.NETWORK_HEALTH,
      routeName: PATHS.PROGRAMMATIC.BASE,
      subItems: false,
      title: 'Network Health',
    },
    [PATHS.PROGRAMMATIC.ADX_HEALTH]: {
      access: (rootGetters) => {
        const isInternalUser = rootGetters['auth/isInternalUser']
        const hasHealthAdx = rootGetters['tools/hasHealthAdx']
        const isProgrammaticUser = rootGetters['auth/isProgrammaticUser']

        return isInternalUser || (hasHealthAdx && isProgrammaticUser)
      },
      active: false,
      divider: false,
      href(associationId = 'all-associations', subId = 'all-secondary', reportPath = 'all-data') {
        const isAllAssociations = associationId === 'all-associations'
        const action = isAllAssociations ? 'edit' : 'read'
        const subString = makeQueryString(associationId, subId, reportPath, action)
        return `/programmatic/${PATHS.PROGRAMMATIC.ADX_HEALTH}${subString}`
      },
      image: navIcons[PATHS.PROGRAMMATIC.ADX_HEALTH],
      baseReportName: PATHS.PROGRAMMATIC.ADX_HEALTH,
      routeName: PATHS.PROGRAMMATIC.BASE,
      subItems: false,
      title: 'AdX Health',
    },
    [PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING]: {
      access: (rootGetters) => {
        const isInternalUser = rootGetters['auth/isInternalUser']
        const hasYieldPartner = rootGetters['tools/hasYieldPartner']
        const isProgrammaticUser = rootGetters['auth/isProgrammaticUser']

        return isInternalUser || (hasYieldPartner && isProgrammaticUser)
      },
      active: false,
      divider: false,
      href(associationId = 'all-associations', subId = 'all-secondary', reportPath = 'all-data') {
        const isAllAssociations = associationId === 'all-associations'
        const action = isAllAssociations ? 'edit' : 'read'
        const subString = makeQueryString(associationId, subId, reportPath, action)
        return `/programmatic/${PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING}${subString}`
      },
      image: navIcons[PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING],
      baseReportName: PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING,
      routeName: PATHS.PROGRAMMATIC.BASE,
      subItems: false,
      title: 'AdX & OB',
    },
    [PATHS.CUSTOM_ANALYTICS.BASE]: {
      access: (rootGetters) => rootGetters['auth/isCustomAnalyticUser'],
      active: false,
      divider: false,
      href(associationId = 'all-associations', subId = 'all-secondary', reportPath = 'all-data') {
        const subString = makeQueryString(associationId, subId, reportPath)
        return `/${PATHS.CUSTOM_ANALYTICS.BASE}${subString}`
      },
      image: navIcons[PATHS.CUSTOM_ANALYTICS.BASE],
      baseReportName: PATHS.CUSTOM_ANALYTICS.BASE,
      routeName: PATHS.CUSTOM_ANALYTICS.BASE,
      subItems: false,
      title: 'Custom Analytics',
    },
    organization: {
      access: (rootGetters) => {
        const inDemoMode = rootGetters['auth/inDemoMode']

        if (inDemoMode) return false

        const isInternalUser = rootGetters['auth/isInternalUser']

        return isInternalUser
      },
      active: false,
      divider: 'pre',
      href(associationId = 'all-associations', subId = 'all-secondary', reportPath = 'all-data') {
        const action = 'read'
        const subString = makeQueryString(associationId, subId, reportPath, action)
        return `/${PATHS.INTERNAL.BASE}/${PATHS.INTERNAL.ORGANIZATION}${subString}`
      },
      image: navIcons[PATHS.INTERNAL.ORGANIZATION],
      baseReportName: PATHS.INTERNAL.ORGANIZATION,
      routeName: PATHS.INTERNAL.BASE,
      subItems: false,
      title: 'Organization',
    },
    person: {
      access: (rootGetters) => {
        const inDemoMode = rootGetters['auth/inDemoMode']

        if (inDemoMode) return false

        const isInternalUser = rootGetters['auth/isInternalUser']

        return isInternalUser
      },
      active: false,
      divider: false,
      href(associationId = 'all-associations', subId = 'all-secondary', reportPath = 'all-data') {
        const action = 'read'
        const subString = makeQueryString(associationId, subId, reportPath, action)
        return `/${PATHS.INTERNAL.BASE}/${PATHS.INTERNAL.PERSON}${subString}`
      },
      image: navIcons[PATHS.INTERNAL.PERSON],
      baseReportName: PATHS.INTERNAL.PERSON,
      routeName: PATHS.INTERNAL.BASE,
      subItems: false,
      title: 'Person',
    },
    project: {
      access: (rootGetters) => {
        const inDemoMode = rootGetters['auth/inDemoMode']

        if (inDemoMode) return false

        const isInternalUser = rootGetters['auth/isInternalUser']

        return isInternalUser
      },
      active: false,
      divider: false,
      href(associationId = 'all-associations', subId = 'all-secondary', reportPath = 'all-data') {
        const action = 'read'
        const subString = makeQueryString(associationId, subId, reportPath, action)
        return `/${PATHS.INTERNAL.BASE}/${PATHS.INTERNAL.PROJECT}${subString}`
      },
      image: navIcons[PATHS.INTERNAL.PROJECT],
      baseReportName: PATHS.INTERNAL.PROJECT,
      routeName: PATHS.INTERNAL.BASE,
      subItems: false,
      title: 'Project',
    },
    [PATHS.INTERNAL.TIME_ROLLUP]: {
      access: (rootGetters) => {
        const inDemoMode = rootGetters['auth/inDemoMode']

        if (inDemoMode) return false

        const isInternalUser = rootGetters['auth/isInternalUser']

        return isInternalUser
      },
      active: false,
      divider: false,
      href(associationId = 'all-associations', subId = 'all-secondary', reportPath = 'month-to-date') {
        const action = 'read'
        const subString = makeQueryString(associationId, subId, reportPath, action)
        return `/${PATHS.INTERNAL.BASE}/${PATHS.INTERNAL.TIME_ROLLUP}${subString}`
      },
      image: navIcons[PATHS.INTERNAL.TIME_ROLLUP],
      baseReportName: PATHS.INTERNAL.TIME_ROLLUP,
      routeName: PATHS.INTERNAL.BASE,
      subItems: false,
      title: 'Time Rollup',
    },
    [PATHS.INTERNAL.TIME_SHEET]: {
      access: (rootGetters) => {
        const inDemoMode = rootGetters['auth/inDemoMode']

        if (inDemoMode) return false

        const isInternalUser = rootGetters['auth/isInternalUser']

        return isInternalUser
      },
      active: false,
      divider: false,
      href(associationId = 'all-associations', subId = 'all-secondary', reportPath = 'this-week') {
        const action = 'read'
        const subString = makeQueryString(associationId, subId, reportPath, action)
        return `/${PATHS.INTERNAL.BASE}/${PATHS.INTERNAL.TIME_SHEET}${subString}`
      },
      image: navIcons[PATHS.INTERNAL.TIME_SHEET],
      baseReportName: PATHS.INTERNAL.TIME_SHEET,
      routeName: PATHS.INTERNAL.BASE,
      subItems: false,
      title: 'Time Sheet',
    },
    [PATHS.DEVELOP.BASE]: {
      access: (rootGetters) => rootGetters['auth/inDevMode'],
      active: false,
      divider: 'pre',
      image: navIcons.docs,
      baseReportName: null,
      routeName: 'develop',
      href(pageName) {
        const subString = (pageName && `/${pageName}`) || ''

        return `/develop${subString}`
      },
      title: 'Develop',
    },
  },
})

export default navigation
